import { useConfiguration } from '@ori/favorites-context';
import { getFavoriteProducts } from '@ori/favorites-fetching';
import { useSWR } from '@ori/fetching';
import type { MappedProductBoxProps } from '@ori/product-box';
import type { Dispatch, SetStateAction } from 'react';

import { useToken } from './useToken';

export interface UseGetFavoritesProductsParams {
  amountToLoad: number;
  loadOffset: number;
  remainingProductCodes: string[];
  sourceAppName: string;
  setGetFavoriteProductsCalled: Dispatch<SetStateAction<boolean>>;
  setLoadedProducts: Dispatch<SetStateAction<MappedProductBoxProps[]>>;
  setAreFavoritesLoaded: Dispatch<SetStateAction<boolean>>;
  setLoadOffset: Dispatch<SetStateAction<number>>;
}

export const useGetFavoritesProducts = ({
  amountToLoad,
  loadOffset,
  remainingProductCodes,
  sourceAppName,
  setAreFavoritesLoaded,
  setGetFavoriteProductsCalled,
  setLoadOffset,
  setLoadedProducts,
}: UseGetFavoritesProductsParams) => {
  const { currentLanguage, graphqlUrl, tenant, customerId } = useConfiguration();
  const { token } = useToken();

  return useSWR(
    [amountToLoad, loadOffset, remainingProductCodes, token],
    async (
      amount: number,
      offset: number,
      productCodes: string[],
      formattedToken?: string,
      // eslint-disable-next-line max-params -- Can't pass object to useSWR keys, would not trigger hook as expected.
    ) => {
      const nextProductCodes = productCodes.slice(offset, amount);
      if (nextProductCodes.length && formattedToken) {
        setGetFavoriteProductsCalled(true);

        const mappedProductBoxProps = await getFavoriteProducts({
          currentLanguage,
          graphqlUrl,
          sourceAppName,
          tenant,
          token: formattedToken,
          customerId,
          productCodes: nextProductCodes,
        });

        if (Array.isArray(mappedProductBoxProps)) {
          setLoadedProducts((prevProducts) => [...prevProducts, ...mappedProductBoxProps]);
          setAreFavoritesLoaded(true);
          setLoadOffset(amount);
        }
      }
    },
  );
};
